import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import {
  Button,
  Badge,
  Container,
  Dropdown,
  Form,
  Collapse,
  Row,
  Col,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiFilter, FiX } from "react-icons/fi";
import { useFetchReports, useFetchOrders } from "./api/reports";
import Pagination from "./Pagination";
import Preloader from "./Preloader";
import Table from "./Table";

const settings = {
  perPage: [20, 50, 100],
};

const TableRow = (props) => {
  const { report } = props;

  return (
    <tr>
      <td className="d-none d-md-table-cell">{report.name}</td>
      <td className="d-none d-xl-table-cell">{report.carts}</td>
      <td className="d-none d-md-table-cell">{report.orders}</td>
      <td className="d-none d-xl-table-cell">
        {(100 * report.orders) / report.carts} %
      </td>
      <td className="d-none d-xl-table-cell">{report.total_cart_value}</td>
      <td className="d-none d-xl-table-cell">{report.total_order_value}</td>
    </tr>
  );
};

export const Reports = () => {
  const [defaultLimit, setDefaultLimit] = useState(settings.perPage[0]);
  const [startCreatedDate, setStartCreatedDate] = useState();
  const [endCreatedDate, setEndCreatedDate] = useState();
  const [filters, setFilters] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [formatFilters, setFormatFilters] = useState([]);

  const {
    data: reportsData,
    isLoading,
    error,
    isError,
  } = useFetchReports(filters);

  const [reports, setReports] = useState([]);
  const [sum, setSum] = useState([]);
  const [previousUrl, setPreviousUrl] = useState("");
  const [nextUrl, setNextUrl] = useState("");
  const loading = isLoading;

  useEffect(() => {
    if (reportsData) {
      setReports(reportsData.items);
      setSum(reportsData.sum);

      const params = `limit=${defaultLimit}`;

      if (reportsData.links.next) {
        setNextUrl(`${reportsData.links.next}&${params}`);
      } else {
        setNextUrl("");
      }
      if (reportsData.links.prev) {
        setPreviousUrl(`${reportsData.links.prev}&${params}`);
      } else {
        setPreviousUrl("");
      }
    }
  }, [reportsData, defaultLimit]);

  if (isError) {
    return <div>{error.message}</div>;
  }

  const changePage = (action) => {
    if (action === "next") {
      const page = parseInt(reportsData.links.page, 10) + 1;
      setFilters([
        {
          key: "page",
          value: page,
        },
        {
          key: "limit",
          value: defaultLimit,
        },
      ]);
    }
    if (action === "prev") {
      const page =
        parseInt(reportsData.links.page, 10) > 1
          ? parseInt(reportsData.links.page, 10) - 1
          : 1;
      setFilters([
        {
          key: "page",
          value: page,
        },
        {
          key: "limit",
          value: defaultLimit,
        },
      ]);
    }
  };

  const changePerPage = (value) => {
    setDefaultLimit(value);
    setFilters([
      {
        key: "limit",
        value,
      },
    ]);
  };

  const applyFilters = (target = "") => {
    let queries = [];
    let formatQueries = [];

    if (target !== "created_at" && startCreatedDate) {
      queries = [
        ...queries,
        {
          key: "created_at[from]",
          value: format(startCreatedDate, "yyyy-MM-dd"),
        },
      ];

      const i = formatQueries.findIndex((_item) => _item.key === "created_at");
      if (i > -1) {
        formatQueries[i].child = [
          ...formatQueries[i].child,
          {
            key: "from",
            value: format(startCreatedDate, "yyyy-MM-dd"),
          },
        ];
      } else {
        formatQueries = [
          ...formatQueries,
          {
            key: "created_at",
            label: "Created",
            child: [
              {
                key: "from",
                value: format(startCreatedDate, "yyyy-MM-dd"),
              },
            ],
          },
        ];
      }
    }

    if (target !== "created_at" && endCreatedDate) {
      queries = [
        ...queries,
        {
          key: "created_at[to]",
          value: format(endCreatedDate, "yyyy-MM-dd"),
        },
      ];

      const i = formatQueries.findIndex((_item) => _item.key === "created_at");
      if (i > -1) {
        formatQueries[i].child = [
          ...formatQueries[i].child,
          {
            key: "to",
            value: format(endCreatedDate, "yyyy-MM-dd"),
          },
        ];
      } else {
        formatQueries = [
          ...formatQueries,
          {
            key: "created_at",
            label: "Created",
            child: [
              {
                key: "to",
                value: format(endCreatedDate, "yyyy-MM-dd"),
              },
            ],
          },
        ];
      }
    }

    setFilters(queries);
    setFormatFilters(formatQueries);
  };

  const clearFilters = () => {
    setFilters([]);
    setFormatFilters([]);
    setStartCreatedDate();
    setEndCreatedDate();
  };

  return (
    <div className="mt-4">
      <Container>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <h1 className="fs-2 text-gray-800 lh-1 mb-0">Reports</h1>
          </div>
        </div>
        <Form>
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-primary"
              onClick={() => setShowFilter(!showFilter)}
              aria-controls="filter-collapse-container"
              aria-expanded={showFilter}
            >
              <FiFilter className="me-1" />
              <span className="d-none d-md-inline-block">Filter</span>
            </Button>
            <div className="d-flex">
              <Dropdown align="end">
                <Dropdown.Toggle variant="striped" className="me-3">
                  <BsThreeDotsVertical className="fs-4 text-gray-600" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Header>Show</Dropdown.Header>
                  {settings.perPage.map((value) => (
                    <Dropdown.Item
                      active={value === defaultLimit}
                      key={value}
                      onClick={() => changePerPage(value)}
                    >
                      {value}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Form>
        <Collapse in={showFilter}>
          <Form as={Row} id="filter-collapse-container">
            <Col md="6">
              <Form.Label>Date</Form.Label>
              <Form.Group>
                <Form.Text className="text-muted">From</Form.Text>

                <DatePicker
                  className="form-control py-1 px-2"
                  selected={startCreatedDate}
                  onChange={(date) => setStartCreatedDate(date)}
                  isClearable
                  maxDate={endCreatedDate || new Date()}
                  dateFormat="yyyy-MM-dd"
                />

                <Form.Text className="text-muted">To</Form.Text>

                <DatePicker
                  className="form-control py-1 px-2"
                  selected={endCreatedDate}
                  onChange={(date) => setEndCreatedDate(date)}
                  maxDate={new Date()}
                  minDate={startCreatedDate}
                  isClearable
                  dateFormat="yyyy-MM-dd"
                />
              </Form.Group>
            </Col>
            <Form.Group className="py-2" as={Col} md="12">
              <Button
                variant="primary"
                onClick={() => {
                  applyFilters();
                }}
              >
                Apply Filters
              </Button>
              {filters.length > 0 && (
                <Button
                  type="reset"
                  variant="link"
                  onClick={() => {
                    clearFilters();
                  }}
                >
                  Clear
                </Button>
              )}
              <Button
                variant="Light"
                onClick={() => {
                  clearFilters();
                  setShowFilter(false);
                }}
              >
                Cancel
              </Button>
            </Form.Group>
          </Form>
        </Collapse>
        {formatFilters && formatFilters.length > 0 && (
          <div className="mt-2">
            <small>Active Filters: </small>
            {formatFilters.map((filter) => (
              <span key={filter.key} className="me-2">
                {filter.key !== "created_at" && (
                  <span key={filter.key}>
                    <Badge pill bg="primary py-1  mb-1">
                      <span>
                        {filter.label}: {filter.value}{" "}
                      </span>
                      <Button
                        as={FiX}
                        className="p-0"
                        size="sm"
                        variant="link"
                        onClick={() => {
                          applyFilters(filter.key);
                        }}
                      />
                    </Badge>
                  </span>
                )}
                {filter.key === "created_at" && filter.child && (
                  <span key={filter.key}>
                    <Badge pill bg="primary py-1  mb-1">
                      <span>{filter.label}: </span>
                      {filter.child.map((item, index) => (
                        <span key={index}>
                          {filter.child.length === 1 &&
                            item.key === "from" &&
                            `${item.value} - ...`}
                          {filter.child.length > 1 &&
                            index === 0 &&
                            item.key === "from" &&
                            item.value}
                          {index === 0 &&
                            item.key === "to" &&
                            `... - ${item.value}`}
                          {index === 1 &&
                            item.key === "to" &&
                            ` - ${item.value}`}
                        </span>
                      ))}
                      <Button
                        as={FiX}
                        className="p-0"
                        size="sm"
                        variant="link"
                        onClick={() => {
                          if (filter.key === "created_at") {
                            setStartCreatedDate();
                            setEndCreatedDate();
                          }
                          applyFilters(filter.key);
                        }}
                      />
                    </Badge>
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
        <Table striped bordered hover className="mt-3">
          <thead>
            <tr>
              <th className="d-none d-md-table-cell">Salesman group level</th>
              <th className="d-none d-md-table-cell">Carts</th>
              <th className="d-none d-xl-table-cell">Orders</th>
              <th className="d-none d-xl-table-cell">Conv rate</th>
              <th className="d-none d-xl-table-cell">Total cart value</th>
              <th className="d-none d-xl-table-cell">Total order value</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10}>
                  <Preloader />
                </td>
              </tr>
            )}
            {!loading && sum && <TableRow report={sum} />}
            {(!sum || sum.length === 0) && !loading && (
              <tr>
                <td colSpan={6} className="text-start py-4">
                  Can not find any reports
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <Table striped bordered hover className="mt-3">
          <thead>
            <tr>
              <th className="d-none d-md-table-cell">Salesman</th>
              <th className="d-none d-md-table-cell">Carts</th>
              <th className="d-none d-xl-table-cell">Orders</th>
              <th className="d-none d-xl-table-cell">Conv rate</th>
              <th className="d-none d-xl-table-cell">Total cart value</th>
              <th className="d-none d-xl-table-cell">Total order value</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10}>
                  <Preloader />
                </td>
              </tr>
            )}
            {!loading &&
              reports &&
              reports.map((item) => <TableRow key={item.id} report={item} />)}
            {(!reports || reports.length === 0) && !loading && (
              <tr>
                <td colSpan={6} className="text-start py-4">
                  Can not find any reports
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6}>
                <Pagination
                  previousUrl={previousUrl}
                  nextUrl={nextUrl}
                  changePage={changePage}
                />
              </td>
            </tr>
          </tfoot>
        </Table>
      </Container>
    </div>
  );
};

ChartJS.register(ArcElement, Tooltip, Legend);

const App = () => {
  const { data: ordersData } = useFetchOrders();
  const [orders, setOrders] = useState();

  useEffect(() => {
    if (ordersData) {
      let dataPie = {
        labels: [" Web orders (%)", " Salesgroup (%)"],
        datasets: [
          {
            label: "# of Orders",
            data: [ordersData.weborders, ordersData.salereporders],
            backgroundColor: ["#0dcc45", "#1a73e8"],
          },
        ],
      };
      setOrders(dataPie);
    }
  }, [ordersData]);

  return (
    <>
      {orders && (
        <div className="d-flex justify-content-center">
          <div
            style={{
              width: 500,
              height: 500,
            }}
          >
            <Pie data={orders} />
          </div>
        </div>
      )}
      <Reports />
    </>
  );
};
export default App;
